import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface SiteDetail {
  id: string;
  name: string;
  operator: string;
  state: string;
  mine_type: string;
  voltage_levels: string[];
  eem_name: string;
  eem_contact: string;
  status: string;
  substation_count: number;
  device_count: number;
  report_count: number;
  pending_changes: number;
}

interface NavCard {
  emoji: string;
  title: string;
  description: string;
  path: string;
  badge?: number;
  badgeColor?: string;
}

const SiteOverview: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      try {
        const res = await api.get(`/api/sites/${siteId}`);
        setSite(res.data);
      } catch {
        setError('Failed to load site details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [siteId]);

  const navCards: NavCard[] = site ? [
    {
      emoji: '📁',
      title: 'SKM Model & HV SLDs',
      description: 'Latest live SKM PTW model, library file, and HV single line diagrams',
      path: `/site/${siteId}/documents`,
    },
    {
      emoji: '📋',
      title: 'Protection Reports Library',
      description: 'All protection study reports by area',
      path: `/site/${siteId}/reports`,
    },
    {
      emoji: '🛡️',
      title: 'Protection Testing Library',
      description: 'Injection test reports and results',
      path: `/site/${siteId}/testing`,
    },
    {
      emoji: '⚙️',
      title: 'Protection Setting Database',
      description: 'All device protection settings across the site',
      path: `/site/${siteId}/settings`,
    },
    {
      emoji: '⚠️',
      title: 'Pending Changes',
      description: 'Setting changes requiring attention',
      path: `/site/${siteId}/changes`,
      badge: site.pending_changes,
      badgeColor: '#ff9800',
    },
  ] : [];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Dashboard
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>
            {site?.name ?? 'Site Overview'}
          </div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>
            PROTECTION MANAGEMENT HUB
          </div>
        </div>
        <img
          src="/logo-white.png"
          alt="JMB"
          style={{ height: 32, objectFit: 'contain' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </header>

      <main style={{ flex: 1, padding: 32 }}>
        {loading && (
          <div style={{ textAlign: 'center', color: '#888', padding: 60, fontSize: 15 }}>
            Loading site...
          </div>
        )}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8,
            color: '#f44336', padding: '14px 18px', marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {site && (
          <>
            {/* Nav cards grid — straight into sections */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 20,
            }}>
              {navCards.map((card) => (
                <NavCardItem
                  key={card.path}
                  card={card}
                  onClick={() => navigate(card.path)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

const NavCardItem: React.FC<{ card: { emoji: string; title: string; description: string; badge?: number; badgeColor?: string }; onClick: () => void }> = ({ card, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 12,
        border: hovered ? '1.5px solid #0f3460' : '1.5px solid #e8eaf0',
        boxShadow: hovered ? '0 6px 24px rgba(15,52,96,0.14)' : '0 2px 10px rgba(0,0,0,0.06)',
        padding: '24px 26px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
      }}
    >
      <span style={{ fontSize: 32, lineHeight: 1 }}>{card.emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
        }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>{card.title}</span>
          {card.badge !== undefined && card.badge > 0 && (
            <span style={{
              background: card.badgeColor || '#0f3460',
              color: '#fff',
              borderRadius: 20,
              padding: '1px 9px',
              fontSize: 11,
              fontWeight: 700,
              minWidth: 20,
              textAlign: 'center',
            }}>
              {card.badge}
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
          {card.description}
        </p>
      </div>
      <span style={{ color: '#0f3460', fontSize: 18, marginTop: 4, opacity: hovered ? 1 : 0.4, transition: 'opacity 0.2s' }}>›</span>
    </div>
  );
};

export default SiteOverview;
